import { createContext, ReactNode, useContext, useMemo } from "react";
import { VariableOption } from "@/types/formula";

/**
 * What the document being edited adds to every formula input under it.
 *
 * The variable catalogue is process-wide — `/dialer/variables` answers the same list for
 * every workflow — so it cannot hold the names *one* workflow declares. Those travel with
 * the document instead (`graph.variables`, already shaped as catalogue rows by the server),
 * and a screen editing a document provides them here once. Every `FormulaField`,
 * `FormulaEditor` and path picker below then suggests them and has them checked, without
 * any of those components knowing what a workflow is.
 *
 * Two lists because they serve two readers:
 * - `variables` are catalogue rows, appended to the fetched list by `useVariableCatalog`;
 * - `declarations` are the raw declarations, echoed to `POST /dialer/check-formula` so a
 *   name declared a second ago is not underlined as unknown while the draft is saving.
 */
export type FormulaScope = {
    variables: VariableOption[];
    declarations: Record<string, unknown>[];
};

const EMPTY: FormulaScope = { variables: [], declarations: [] };

const FormulaScopeContext = createContext<FormulaScope>(EMPTY);

export function FormulaScopeProvider({ variables, declarations, children }: Partial<FormulaScope> & { children: ReactNode }) {
    const value = useMemo(() => ({ variables: variables ?? [], declarations: declarations ?? [] }), [variables, declarations]);
    return <FormulaScopeContext.Provider value={value}>{children}</FormulaScopeContext.Provider>;
}

export function useFormulaScope(): FormulaScope {
    return useContext(FormulaScopeContext);
}

/**
 * The fetched catalogue followed by the document's rows, first spelling of a path winning.
 *
 * First rather than last for the server's reason (`VariableCatalog.merge`): a name a step
 * publishes keeps its own type and wording, and the publication check refuses the clash.
 */
export function withScopeVariables(fetched: VariableOption[], extra: VariableOption[]): VariableOption[] {
    if (extra.length === 0) return fetched;
    const seen = new Set(fetched.map(option => option.value));
    const added: VariableOption[] = [];
    extra.forEach(option => {
        if (!option?.value || seen.has(option.value)) return;
        seen.add(option.value);
        added.push(option);
    });
    return added.length === 0 ? fetched : [...fetched, ...added];
}
